import { InputfieldTypes } from "../../../../../../enums/InputFieldAttributesEnums";
import InputField from "../../../../../../reusable-components/TNInputField";
import styles from "./EditMaterialsForm.module.scss";
import { dropDownFields, getEditJobFields } from "./constants";
import { useEffect, useState } from "react";
import { Select } from "antd";
import { useFormContext } from "react-hook-form";
import { allowedKeys } from "../../../../../../constants/keyboardKeys";
import {
  stripCurrencyFormatting,
  thousandsSeparator,
} from "../../../../../../Utilities/regex";
import { formatNumber } from "../../../../../../utils/numberFormatterUtil";

const EditMaterialsForm = ({
  defaultValues,
  setEditedValues,
  setSelectedRow,
}) => {
  const { watch, setValue, clearErrors } = useFormContext();
  const materialCost = watch("materialCost");
  const materialPrice = watch("materialPrice");
  const truckingCost = watch("truckingCost");
  const truckingPrice = watch("truckingPrice");
  const landedPrice = watch("landedPrice");
  const minimumHours = watch("minimumHours");
  const quantity = watch("quantity");

  const { Option } = Select;
  const { TON_TXT, LOAD_TXT, HOUR_TXT } = dropDownFields;
  const [selectedMaterialUnit, setSelectedMaterialUnit] = useState(
    defaultValues?.materialCostType ?? TON_TXT
  );
  const handleMaterialUnitChange = (value: string) => {
    setSelectedMaterialUnit(value);
  };
  const selectMaterialCostAfterFieldProps = (
    <Select
      value={selectedMaterialUnit}
      onChange={handleMaterialUnitChange}
      className={styles.selectSection}
      disabled
    >
      <Option value={TON_TXT}>
        {<span className={styles.unitContainer}>{TON_TXT}</span>}
      </Option>
      <Option value={LOAD_TXT}>
        {<span className={styles.unitContainer}>{LOAD_TXT}</span>}
      </Option>
    </Select>
  );

  const [selectedMaterialPriceUnit, setSelectedMaterialPriceUnit] = useState(
    defaultValues?.materialPriceType ?? TON_TXT
  );
  const handleMaterialPriceUnitChange = (value: string) => {
    setSelectedMaterialPriceUnit(value);
  };
  const selectMaterialPriceAfterFieldProps = (
    <Select
      value={selectedMaterialPriceUnit}
      onChange={handleMaterialPriceUnitChange}
      className={styles.selectSection}
    >
      <Option value={TON_TXT}>
        {<span className={styles.unitContainer}>{TON_TXT}</span>}
      </Option>
      <Option value={LOAD_TXT}>
        {<span className={styles.unitContainer}>{LOAD_TXT}</span>}
      </Option>
    </Select>
  );

  const [selectedTruckingUnit, setSelectedTruckingUnit] = useState(
    defaultValues?.truckingCostType ?? TON_TXT
  );
  const handleTruckingUnitChange = (value: string) => {
    setSelectedTruckingUnit(value);
    setSelectedTruckingPriceUnit(value);
    setSelectedLandedPriceUnit(value);
  };
  const selectTruckingAfterFieldProps = (
    <Select
      value={selectedTruckingUnit}
      onChange={handleTruckingUnitChange}
      className={styles.selectSection}
    >
      <Option value={TON_TXT}>
        {<span className={styles.unitContainer}>{TON_TXT}</span>}
      </Option>
      <Option value={LOAD_TXT}>
        {<span className={styles.unitContainer}>{LOAD_TXT}</span>}
      </Option>
      <Option value={HOUR_TXT}>
        {<span className={styles.unitContainer}>{HOUR_TXT}</span>}
      </Option>
    </Select>
  );

  const [selectedTruckingPriceUnit, setSelectedTruckingPriceUnit] = useState(
    defaultValues?.truckingPriceType ?? TON_TXT
  );
  const handleTruckingPriceUnitChange = (value: string) => {
    setSelectedTruckingUnit(value);
    setSelectedTruckingPriceUnit(value);
    setSelectedLandedPriceUnit(value);
    if (value !== HOUR_TXT) setValue("minimumHours", undefined);
  };
  const selectTruckingPriceAfterFieldProps = (
    <Select
      value={selectedTruckingPriceUnit}
      onChange={handleTruckingPriceUnitChange}
      className={styles.selectSection}
    >
      <Option value={TON_TXT}>
        {<span className={styles.unitContainer}>{TON_TXT}</span>}
      </Option>
      <Option value={LOAD_TXT}>
        {<span className={styles.unitContainer}>{LOAD_TXT}</span>}
      </Option>
      <Option value={HOUR_TXT}>
        {<span className={styles.unitContainer}>{HOUR_TXT}</span>}
      </Option>
    </Select>
  );

  const [selectedLandedPriceUnit, setSelectedLandedPriceUnit] = useState(
    defaultValues?.landedPriceType ?? TON_TXT
  );
  const handleLandedPriceUnitChange = (value: string) => {
    setSelectedTruckingUnit(value);
    setSelectedTruckingPriceUnit(value);
    setSelectedLandedPriceUnit(value);
  };
  const selectLandedPriceAfterFieldProps = (
    <Select
      value={selectedLandedPriceUnit}
      onChange={handleLandedPriceUnitChange}
      className={styles.selectSection}
      disabled
    >
      <Option value={TON_TXT}>
        {<span className={styles.unitContainer}>{TON_TXT}</span>}
      </Option>
      <Option value={LOAD_TXT}>
        {<span className={styles.unitContainer}>{LOAD_TXT}</span>}
      </Option>
      <Option value={HOUR_TXT}>
        {<span className={styles.unitContainer}>{HOUR_TXT}</span>}
      </Option>
    </Select>
  );

  const handleTruckingPriceCost = (isTruckPrice: boolean, markup: number) => {
    const [truckingVal, fieldName, adjacentFieldName] = isTruckPrice
      ? [
          stripCurrencyFormatting(truckingPrice),
          "truckingCost",
          "truckingPrice",
        ]
      : [
          stripCurrencyFormatting(truckingCost),
          "truckingPrice",
          "truckingCost",
        ];
    if (truckingVal !== undefined && truckingVal !== null) {
      const val = (truckingVal * markup)?.toFixed(2);
      setValue(fieldName, !isNaN(val) ? formatNumber(val) : "");
      setValue(
        adjacentFieldName,
        formatNumber(Number(truckingVal)?.toFixed(2))
      );
    }
  };

  const onKeyDownHandler = (e) => {
    // Allow control and navigation keys
    if (
      allowedKeys?.includes(e?.key) ||
      e?.ctrlKey ||
      e?.altKey ||
      e?.metaKey
    ) {
      return;
    }

    // Allow digitls
    if (/^\d$/.test(e?.key)) {
      return;
    }

    // Allow decimal point if enabled
    if (e?.key === ".") {
      const currentValue = e?.target?.value?.toString() || "";
      if (currentValue.includes(".")) {
        e?.preventDefault();
        return;
      }
      return;
    }

    e?.preventDefault();
  };

  const onNumericBlur = (fieldName) => (e) => {
    if (e.target.value) {
      const stripped = stripCurrencyFormatting(e.target.value);
      const value = formatNumber(Number(stripped).toFixed(2));
      if (!isNaN(Number(stripped))) {
        setValue(fieldName, formatNumber(Number(stripped).toFixed(2)));
      } else {
        setValue(fieldName, null);
      }
      clearErrors(fieldName);
    }
  };

  const editJobFields = getEditJobFields({
    selectMaterialCostAfterFieldProps,
    selectMaterialPriceAfterFieldProps,
    selectTruckingAfterFieldProps,
    selectTruckingPriceAfterFieldProps,
    selectLandedPriceAfterFieldProps,
    handleTruckingPriceCost,
    selectedTruckingPriceUnit,
    onKeyDownHandler,
    onNumericBlur,
  });

  useEffect(() => {
    const [landingPrice, landedPriceType] =
      selectedMaterialPriceUnit === selectedTruckingPriceUnit
        ? [
            (
              Number(stripCurrencyFormatting(materialPrice)) +
              Number(stripCurrencyFormatting(truckingPrice))
            ).toFixed(2),
            selectedLandedPriceUnit,
          ]
        : ["-", null];
    setValue("landedPrice", formatNumber(landingPrice));
    setEditedValues({
      materialCost: stripCurrencyFormatting(materialCost),
      materialPrice: stripCurrencyFormatting(materialPrice),
      landedPrice: landingPrice,
      truckingCost: stripCurrencyFormatting(truckingCost),
      truckingPrice: stripCurrencyFormatting(truckingPrice),
      materialQuantity: stripCurrencyFormatting(quantity),
      minimumHours: minimumHours ?? undefined,
      materialCostType: selectedMaterialUnit,
      materialPriceType: selectedMaterialPriceUnit,
      truckingCostType: selectedTruckingUnit,
      truckingPriceType: selectedTruckingPriceUnit,
      landedPriceType: landedPriceType,
    });
    setSelectedRow(defaultValues?.id);
  }, [
    materialCost,
    materialPrice,
    truckingCost,
    truckingPrice,
    quantity,
    minimumHours,
    selectedMaterialUnit,
    selectedMaterialPriceUnit,
    selectedTruckingUnit,
    selectedTruckingPriceUnit,
    selectedLandedPriceUnit,
  ]);
  return (
    <div className={styles.EditMaterialFormContainer}>
      {editJobFields.map((field) => (
        <div className={styles.EditMaterialInputContainer}>
          <InputField
            name={field.name}
            type={field.type}
            label={field.label}
            disabled={field.disabled}
            addonAfter={field.addonLater}
            placeholder={field.placeholder}
            numberPrecision={field.numberPrecision}
            onKeyDown={field.onKeyDownHandler}
            handleNumberChange={field.handleNumberChange}
            onBlur={field.onBlur}
            required={field.required}
            prefix={field.prefix}
          />
        </div>
      ))}
    </div>
  );
};

export default EditMaterialsForm;
